import React from 'react';
import { observer } from 'mobx-react-lite';
import {
    UploadWrapper,
    ImgItem,
    ImgPlaceholder,
    UploadButtonsWrapper,
    UploadInput,
    UploadButtonWrapper,
    WarningMessageWrapper,
} from './UploadImage.style';
import { UploadImageState } from 'src/AppState/UploadImageState/UploadImageState';
import { FilesPopup } from 'src/components/FilesPopup/FilesPopup';
import { Label } from 'src/domains/layouts/shared/typography/Content.style';
import { ErrorMessageBar } from 'src/domains/layouts/shared/formComponents/FormComponents.style';
import { Button } from 'src/domains/layouts/shared/buttons/Button';
import { ButtonsWrapper } from 'src/domains/layouts/shared/buttons/Button.style';

interface UploadImgPropsType {
    acceptedFileExtensions?: string;
    callbackCheckSizeFunction?: (url: string | undefined) => void;
    callbackClearFunction?: () => void;
    dataTest?: string;
    hasFullWidthPreview?: boolean;
    title: string;
    uploadImageState: UploadImageState;
    hideDeleteButton?: boolean;
}

export const UploadImg = observer(
    ({
        acceptedFileExtensions,
        title,
        uploadImageState,
        callbackCheckSizeFunction,
        callbackClearFunction,
        hasFullWidthPreview,
        dataTest,
        hideDeleteButton,
    }: UploadImgPropsType) => {
        const uploadWithCallback = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
            await uploadImageState.handleUploadAndChooseImage(e);

            if (callbackCheckSizeFunction !== undefined) {
                callbackCheckSizeFunction(uploadImageState.imgUrl);
            }
        };

        const deleteImage = (): void => {
            uploadImageState.handleDeleteSelectedImage();

            if (callbackClearFunction !== undefined) {
                callbackClearFunction();
            }
        };

        const uploadImage = (e: React.ChangeEvent<HTMLInputElement>): void => {
            const handleCallback =
                callbackCheckSizeFunction === undefined
                    ? uploadImageState.handleUploadAndChooseImage
                    : uploadWithCallback;

            uploadImageState.handleCheckFormat(e, handleCallback);
        };

        const {
            isEmptyError,
            imgErrorMessage,
            imgWarningMessage,
            hideSizeMessage,
            forceUpload,
            handleGetFilesList,
            imgUrl,
            filesState,
            handleChooseImage,
            handleDeleteImage,
        } = uploadImageState;

        if (imgWarningMessage !== null) {
            return (
                <WarningMessageWrapper>
                    <Label>{imgWarningMessage} Consider optimization.</Label>
                    <ButtonsWrapper>
                        <Button onClick={forceUpload}>Continue upload</Button>
                        <Button onClick={hideSizeMessage}>Back and optimize</Button>
                    </ButtonsWrapper>
                </WarningMessageWrapper>
            );
        }

        return (
            <React.Fragment>
                <Label>{title}</Label>
                {isEmptyError ? <ErrorMessageBar>Can&apos;t be empty</ErrorMessageBar> : null}
                {imgErrorMessage === null ? null : <ErrorMessageBar>{imgErrorMessage}</ErrorMessageBar>}

                <UploadWrapper hasFullWidthPreview={hasFullWidthPreview}>
                    {imgUrl === '' ? (
                        <ImgPlaceholder hasFullWidthPreview={hasFullWidthPreview}>No image</ImgPlaceholder>
                    ) : (
                        <ImgItem
                            src={imgUrl}
                            hasFullWidthPreview={hasFullWidthPreview}
                            data-test={`${dataTest}-preview`}
                        />
                    )}

                    <UploadButtonsWrapper>
                        <UploadButtonWrapper hasFullWidthPreview={hasFullWidthPreview}>
                            <UploadInput
                                accept={
                                    acceptedFileExtensions ??
                                    'image/jpeg, image/png, image/gif, image/webp, image/svg+xml'
                                }
                                type='file'
                                onChange={uploadImage}
                                data-test={`${dataTest}-file-input`}
                            />
                            <Button>Upload</Button>
                        </UploadButtonWrapper>

                        <Button onClick={handleGetFilesList}>Library</Button>
                        <Button
                            version='primary'
                            status='delete'
                            onClick={deleteImage}
                        >
                            Delete
                        </Button>
                    </UploadButtonsWrapper>
                </UploadWrapper>

                {filesState.showLibraryPopUp === true ? (
                    <FilesPopup
                        title={title}
                        filesState={filesState}
                        onChooseImage={handleChooseImage}
                        onDeleteImage={handleDeleteImage}
                        dataTest='image-library'
                        hideDeleteButton={hideDeleteButton}
                    />
                ) : null}
            </React.Fragment>
        );
    }
);
